import { useCallback, useEffect, useState } from 'react';
import { HALL_LIMITS, NotAHostError, RoomClosedToYouError, SocialAuthError, hallControl, hallPlaces, leadsHall, ownsHall } from '@versesverse/core';
import type { BannedReaderView, HallChallenge, HallStanding, HallEdits, HallLink, HallLogEntry, HallManagedMemberView, HallView, ModerationAction, RoomView, SessionView } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';
import { readableTime, sessionWhenText, useMinuteTick } from './sessionTime';
import { HallSettings } from './HallSettings';
import { refusalWords } from '../i18n/refusal';
import { useOneAtATime } from './useOneAtATime';

/** What the Hall page reads off the social client. */
export interface HallPageClient {
  hall(slug: string): Promise<HallView | null>;
  /** The Hall's live rooms, across every node — the one list that crosses the map. */
  listHallRooms(hallId: string): Promise<RoomView[]>;
  /** Who is waiting to be let in. Leaders only, and empty for a Hall that admits anyone. */
  hallPending(hallId: string, options?: { after?: string; afterId?: string }): Promise<HallManagedMemberView[]>;
  approveMember(hallId: string, userId: string): Promise<unknown>;
  /** The active roster with names — what choosing a co-host needs. Leaders only. */
  hallRoster(hallId: string, options?: { after?: string; afterId?: string }): Promise<HallManagedMemberView[]>;
  /** Rooms asking this Hall to co-host them, unanswered — leadership only (D22). */
  coHostInvitations(hallId: string, options?: { before?: string; beforeId?: string }): Promise<RoomView[]>;
  acceptCoHost(roomId: string, hallId: string): Promise<RoomView>;
  endCoHost(roomId: string): Promise<RoomView>;
  /** The record of what this Hall's moderators did — leadership only (D22). */
  hallLog(hallId: string, options?: { before?: string; beforeId?: string }): Promise<HallLogEntry[]>;
  /* Barring somebody from the community — answered by the server since Halls existed, and
     reachable from no client until now. Leadership only, and it reaches the live rooms. */
  banFromHall(hallId: string, userId: string): Promise<void>;
  unbanFromHall(hallId: string, userId: string): Promise<void>;
  /* Whom it barred. `HallView` omits that list on purpose, so this is the only way to see it. */
  hallBanned(hallId: string): Promise<BannedReaderView[]>;
  setHallModerator(hallId: string, userId: string, isModerator: boolean): Promise<HallView>;
  /** Its upcoming sessions — the half of the page that is true when nobody is talking. */
  listHallSessions(hallId: string): Promise<SessionView[]>;
  /** Where the asking reader stands — the read that decides which single control to draw. */
  hallStanding(hallId: string): Promise<HallStanding>;
  joinHall(hallId: string): Promise<unknown>;
  leaveHall(hallId: string): Promise<void>;
  startHallVerification(hallId: string, handle: string): Promise<HallChallenge>;
  checkHallVerification(hallId: string): Promise<HallView>;
  editHall(hallId: string, edits: HallEdits): Promise<HallView>;
  exportHall(hallId: string): Promise<unknown>;
  /** Deletion is real — the rooms go, the roster goes (D21.4). Owner only. */
  deleteHall(hallId: string): Promise<void>;
  signInUrl(): string;
}

export interface HallPageProps {
  client: HallPageClient;
  slug: string;
  /** Our own user id, so the page knows whether we own it. Null when signed out. */
  meId: string | null;
  /**
   * A node's name, resolved by the CALLER.
   *
   * The social plane stores node ids and never node names (D10), so the Hall page cannot know what
   * "abraham" is called in the reader's language — the content side can, and does. Falling back to
   * the id is the honest failure: a room in a bundle this reader has not loaded still lists.
   */
  nodeName(nodeId: string): string;
  /** Walk into a room by going to the node it is on, where the room controls already live. */
  onGoToNode(nodeId: string): void;
  onClose(): void;
  /**
   * The hall's name, once it is known — so the TAB can say it.
   *
   * `nav/title.ts` exists because every view left the tab reading "Verses Verse — the scriptures,
   * cross-linked": ten bookmarks are ten identical rows and history search for a name finds
   * nothing. It reaches every view but this one, because `App` is handed a SLUG and the name lives
   * here, three fetches later.
   *
   * That matters more here than anywhere: this page is what a link in a YouTube description lands
   * on, which is the one job the file's own header gives it. Optional, because the page must render
   * for a caller that does not care what the tab says.
   */
  onNamed?(name: string): void;
}

/*
 * A LEADER'S PAGE (COMMUNITY.md §4).
 *
 * The thing they put in a YouTube description, so it is **public to read** — putting it behind an
 * account would make it useless for the one job it has.
 *
 * Its links are rendered plainly and go directly where they point (D21.2). That is the opposite of
 * the universal platform behaviour of burying links that send members away, and it is a promise a
 * leader can check in ten seconds — so no `rel="nofollow"`, no interstitial, no rewriting.
 *
 * The owner also sees the two things D21 makes structural: verification, and **the door out**.
 * Export is offered plainly rather than hidden in a settings menu, because a door with no visible
 * lock is the point.
 */
/**
 * One phrase per recorded action.
 *
 * A `Record` rather than a template built from the action name, so a tenth action fails to compile
 * here until somebody decides what it says — and so nothing constructs a dictionary key out of a
 * string that arrived over the wire.
 */
export const LOG_SAYS: Record<ModerationAction, keyof Strings> = {
  mute: 'log.mute',
  onStage: 'log.onStage',
  offStage: 'log.offStage',
  remove: 'log.remove',
  ban: 'log.ban',
  unban: 'log.unban',
  addHost: 'log.addHost',
  silence: 'log.silence',
  closeRoom: 'log.closeRoom',
  doorLocked: 'log.doorLocked',
  doorOpened: 'log.doorOpened',
  moderatorAdded: 'log.moderatorAdded',
  moderatorRemoved: 'log.moderatorRemoved',
  hallBanned: 'log.hallBanned',
  hallUnbanned: 'log.hallUnbanned',
  coHostAsked: 'log.coHostAsked',
  coHostAccepted: 'log.coHostAccepted',
  coHostEnded: 'log.coHostEnded',
  /*
   * The SITE's own two, which cannot reach this page: they are recorded under no Hall, and this log
   * is read by `hallId`. They are here because the `Record` is TOTAL on purpose — a new action must
   * fail to compile until somebody decides what it says — and answering that with a phrase costs
   * less than carving an exception into the type and losing the check for every future action.
   */
  readerSuspended: 'reports.wasSuspended',
  readerReinstated: 'reports.wasReinstated',
};

/**
 * WHAT A LOG ROW SAYS — read through a lookup, because the action comes off the WIRE.
 *
 * `LOG_SAYS` is total over `ModerationAction` and that is worth keeping: a new act must fail to
 * compile until somebody decides what it says, which is how `moderatorAdded` got words in three
 * languages the hour it was added. **But totality is a claim about this bundle, not about the
 * server**, and the two are not the same age. A browser holding a cached app reads a record
 * written by a server that has since learnt a new act — which happened this very session, twice.
 *
 * Indexed by name the answer was `undefined`, and `t(undefined, { name })` is not a blank row: it
 * is `undefined.replace(...)` inside `fill`, a TypeError thrown during render. **The audit log
 * D22 calls the point of a Hall would have taken the page down with it.**
 *
 * So an act this bundle cannot name is still a row: who, to whom, and when are all in hand, and
 * the sentence says the one thing that is actually true — that this page is older than the
 * record. The same bargain the phrase dictionary makes with English, and `refusalKey` with a
 * refusal it does not know.
 */
const SAID: Partial<Record<string, keyof Strings>> = LOG_SAYS;

export function saidBy(action: string): keyof Strings {
  return SAID[action] ?? 'log.unnamed';
}

export function HallPage({ client, slug, meId, nodeName, onGoToNode, onClose, onNamed }: HallPageProps): React.JSX.Element {
  const { t, lang } = useLang();
  const [hall, setHall] = useState<HallView | null>(null);
  /*
   * Told once the name is known, from wherever it was learned — the first load and the reload after
   * an edit both land in `hall`, so watching the value covers both without either knowing about the
   * tab. It fires again if a host renames the hall, which is right: the tab is stale otherwise.
   */
  useEffect(() => {
    if (hall?.name) onNamed?.(hall.name);
  }, [hall?.name, onNamed]);
  /*
   * WHETHER WE COULD TELL WHERE THIS READER STANDS — which is not the same as `none`.
   *
   * `hallStanding` answered `none` for a stranger, for a signed-out reader AND for a failed read,
   * and `hallControl` is a total function over what it is given: so a MEMBER whose standing read
   * failed was offered "Join" the community they are already in. The client now tells the cases
   * apart, and this is the half that acts on the difference — a control that cannot work is worse
   * than an absent one, which is the rule this whole block was rewritten around.
   */
  const [standingUnknown, setStandingUnknown] = useState(false);
  /** Whether the LAST read failed — never the same thing as there being no such community. */
  const [unreadable, setUnreadable] = useState(false);
  const [rooms, setRooms] = useState<RoomView[]>([]);
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [pending, setPending] = useState<HallManagedMemberView[]>([]);
  /** Where THIS reader stands with this Hall — "none" until the first read answers. */
  /* Once a minute, so a study's countdown on this page counts — the same hook the strip uses. */
  const minuteTick = useMinuteTick();
  const [standing, setStanding] = useState<HallStanding>({ status: 'none', role: null });
  const [roster, setRoster] = useState<HallManagedMemberView[]>([]);
  /** What the mod team has done — leadership only, and read on the page rather than acted on. */
  const [log, setLog] = useState<HallLogEntry[]>([]);
  /** Whether the last page came back empty — the end of the record, so "earlier" goes away. */
  const [logEnded, setLogEnded] = useState(false);
  /*
   * COULD NOT BE READ, which is not the same as EMPTY — the rule this repository has stated since
   * `liveRooms` was written and which three reads here still swallowed.
   *
   * The roster, the barred list and the moderation log each answered a failed request with `[]`.
   * The barred section is drawn only when the list has entries and the log only when it has rows,
   * so ONE failed request told a leader that nobody is barred, that their team has done nothing,
   * and — because an empty page also set `logEnded` — that the record is complete. The supervision
   * tool lying by omission, and quietly.
   *
   * A flag each, because a leader should be told WHICH part is missing: the team, the record and
   * the queue at the door are read separately and fail separately.
   */
  const [rosterUnread, setRosterUnread] = useState(false);
  /*
   * WHETHER EITHER LIST HAS BEEN READ TO ITS END.
   *
   * Both are pages — a hundred rows against a ceiling of a thousand — and both carry the controls
   * that are the only reason a leader opens them: barring and appointing on the roster, admitting
   * and refusing on the queue. A cap with no way past it made every one of those unreachable for
   * a member past the first page, which is the act itself made impossible rather than a list cut
   * short.
   */
  const [rosterEnded, setRosterEnded] = useState(false);
  const [pendingEnded, setPendingEnded] = useState(false);
  /* And the co-host requests, which are the third list on this page whose rows carry a decision. */
  const [invitationsEnded, setInvitationsEnded] = useState(false);
  const [logUnread, setLogUnread] = useState(false);
  /*
   * And the two that nothing else in the product can report: the queue at the door, and a request
   * from another community to co-host. Both are somebody ELSE asking, and a read that fails on
   * one of those is the only witness to the asking going quiet.
   */
  const [pendingUnread, setPendingUnread] = useState(false);
  const [invitationsUnread, setInvitationsUnread] = useState(false);
  /* And what this community has PROMISED, which is the strip a stranger came for. */
  const [sessionsUnread, setSessionsUnread] = useState(false);

  /** Whom this community has barred — leadership only, and the read that gives the ban an undo. */
  const [barred, setBarred] = useState<BannedReaderView[]>([]);
  /** Rooms that have asked this Hall to co-host them and are still waiting for an answer. */
  const [invitations, setInvitations] = useState<RoomView[]>([]);
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState<HallChallenge | null>(null);
  const [handle, setHandle] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  /** The delete confirmation, which asks the owner to type the address before it will fire. */
  const [deleting, setDeleting] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');

  /**
   * Where this reader stands, asked once and failing on its own terms.
   *
   * Separate from the Hall's own read because the two fail differently and cost differently: a
   * Hall that cannot be read leaves nothing to draw, while a standing that cannot be read leaves a
   * page that is entirely fine except for one button.
   */
  const tellMeWhereIStand = useCallback(async (found: HallView | null): Promise<void> => {
    if (!found) {
      setStanding({ status: 'none', role: null });
      setStandingUnknown(false);
      return;
    }
    try {
      setStanding(await client.hallStanding(found.id));
      setStandingUnknown(false);
    } catch {
      setStandingUnknown(true);
    }
  }, [client]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const found = await client.hall(slug);
      setHall(found);
      setUnreadable(false);
      /* And where this reader stands with it, which is what decides whether they are offered to
         join, told they are waiting, or offered to leave. */
      await tellMeWhereIStand(found);
    } catch {
      /*
       * A FAILED READ, which is not the same as no community at that address.
       *
       * `hall` is deliberately left as it was: a reader already looking at a page whose reload
       * failed should keep the page, not be told the community vanished. The notice below is
       * drawn only when there is nothing to show.
       */
      setUnreadable(true);
    } finally {
      setLoading(false);
    }
  }, [client, slug]);

  /**
   * AN ACT THAT CHANGES WHO IS IN THE HALL, AND THE COUNT ABOVE THE ROSTER.
   *
   * Approving somebody, barring them and lifting a bar each moved their own list and left `hall`
   * alone — so a leader let somebody in, watched them leave the waiting queue, and the line above
   * still said their community had one member. The count is a fact about the HALL, not about the
   * list the act happened to edit, and three verbs each had to remember to re-read it. None did.
   *
   * Joining and leaving already called `load()`; this is the same thing, in one place, so the next
   * verb cannot forget. Found by two browsers: one asks to join, the other lets them in.
   */
  const changesTheRoster = (change: () => Promise<void>): Promise<void> =>
    attempt(async () => {
      /*
       * IN A `finally`, because an act can half-land and the page must show what the server says
       * either way.
       *
       * Barring somebody from a community is two writes: the bar, and the ejection from the Hall's
       * live rooms. When the room server is away the bar still holds — it is enforced at the token
       * mint — and the route now says so with a refusal a leader can act on. Reloading only on
       * success left that leader reading a sentence about somebody who was still listed as a member
       * of their community, which is the page disagreeing with the sentence directly under it.
       */
      try {
        await change();
      } finally {
        await load();
        /*
         * EVERYTHING ON THIS PAGE THAT THE ACT CHANGED, and there are three of them.
         *
         * The Hall itself, for the member count above the roster. The ROSTER, because somebody
         * approved is not on the list the leader is looking at until it is read again — so the
         * community said two members over an empty list, and the verbs a leader has on a person had
         * nobody to act on. And the log, which is what the record is for: a leader who had just
         * barred somebody was reading "what the team has done" with the last thing they did missing.
         */
        await loadRoster();
        await loadLog();
      }
    });

  useEffect(() => {
    let live = true;
    setChallenge(null);
    setProblem(null);
    setNeedsSignIn(false);
    client.hall(slug)
      .then(async found => {
        if (!live) return;
        setHall(found);
        setUnreadable(false);
        setLoading(false);
        /* Where this reader stands, asked after the Hall itself so the page draws immediately and
           the membership control appears with the answer rather than guessing one. */
        await tellMeWhereIStand(found);
      })
      /*
       * A FAILED READ SAYS SO — it used to leave `hall` null, and null is what draws "there is no
       * hall at that address". A leader whose request hiccuped was told the community they built
       * does not exist. The client now answers null only for a 404, which IS that sentence, and
       * throws for everything else, which is this branch.
       */
      .catch(() => { if (live) { setUnreadable(true); setLoading(false); } });
    return () => { live = false; };
  }, [client, slug]);

  /*
   * The rooms follow the Hall rather than the slug, because they are keyed on its id — and this is
   * a SECOND request on purpose: a Hall page that waited for a room list to render would show a
   * leader's page late for the sake of a strip that is usually empty.
   *
   * A FAILED READ OF THE ROOMS IS SILENT, and that reason is about the rooms: they are the
   * liveliest thing on the page and the least essential, they are drawn on the map and in two
   * other lists, and an error banner where a quiet strip belongs would make a working page look
   * broken.
   *
   * THE STUDIES ARE NOT THAT, and they inherited the silence by sitting in the same effect. The
   * paragraph over that section says what it is for: *"a Hall is quiet most of the time, so a page
   * that could only show live rooms would show a leader's community as empty whenever nobody
   * happened to be talking"*. A failed read of it puts that impression back — a stranger who has
   * just followed a link from a YouTube description reads a community with nothing planned, which
   * is the one thing this strip exists to prevent, and neither they nor the leader can tell.
   */
  const hallId = hall?.id ?? null;
  useEffect(() => {
    if (hallId === null) return;
    let live = true;
    client.listHallRooms(hallId)
      .then(found => { if (live) setRooms(found); })
      .catch(() => { if (live) setRooms([]); });
    client.listHallSessions(hallId)
      .then(found => { if (live) { setSessions(found); setSessionsUnread(false); } })
      /* Kept, like every other list on this page that says when it failed. */
      .catch(() => { if (live) setSessionsUnread(true); });
    return () => { live = false; };
  }, [client, hallId]);

  /*
   * WHO RUNS THIS COMMUNITY — asked of core rather than decided here.
   *
   * A co-host runs the Hall's door and its rooms; only the owner holds the door OUT (D21.3). Both
   * halves were written inline on this page, which was fine while there was one Hall page. The
   * phone has none yet (§0 carries it as open), and the sentence behind every leader-only read
   * there is — the roster, the record, the queue, the co-host requests, the bar — is the last one
   * that should be typed a second time somewhere else.
   */
  const iOwn = ownsHall(hall, meId);
  const iLead = leadsHall(hall, meId);

  /* How full the community is, or null when the ceiling is a number nobody here can act on. The
     rule is core's, so the phone answers it the same way on the day it grows a Hall page. */
  const places = hall === null ? null : hallPlaces(hall, iLead);

  /**
   * A moment, in the reader's own language.
   *
   * The log drew its dates with `toLocaleString(undefined, …)` — the BROWSER's locale, not the
   * one the reader chose — so somebody reading this page in Hebrew got English month names in the
   * one place the page states a time. The rule is already written for the phone's studies: *"the
   * reader's LANGUAGE is the locale a date is formatted in"*. Shared here so the queue below and
   * the record at the foot cannot drift apart about it.
   */
  /* Through the guard the studies panel has always had: an unparseable time renders as
     "Invalid Date" otherwise — English inside a Hebrew page, and a moderator's act appearing
     to have happened at a non-time. The format is this page's; the guard is nobody's. */
  const moment = (iso: string): string => readableTime(iso, lang, t('shared.timeUnknown'), {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  /*
   * WHO IS WAITING TO BE LET IN.
   *
   * Only asked for by a leader, and only when the Hall actually holds people at the door: a Hall
   * that admits anyone has no queue and should not pay for a request to prove it.
   *
   * A FAILURE WAS SILENT, *"for the same reason the room strip's is — this sits under a working
   * page"*, and that reason describes a different list. A Hall's rooms are drawn in three other
   * places and a reader who misses them here meets them on the map. This queue is drawn NOWHERE
   * else: `roster` and `hallsOf` both filter to `status: 'active'`, which is the whole reason the
   * section below exists — *"the queue existed, nobody could see it"*. So a failed read is not a
   * gap in a page that otherwise works; it is the only witness to somebody standing at the door
   * going quiet, and it looks exactly like nobody being there, while the applicant has joined as
   * far as they know.
   *
   * It keeps what it had and says it could not read — what the roster below and the log below
   * that have both done since the day each was written.
   */
  const loadPending = useCallback(async (): Promise<void> => {
    /*
     * ASKED FOR WHENEVER THIS READER LEADS, not only while the Hall requires approval.
     *
     * It was gated on the policy, so switching approval OFF hid the queue — and whoever was in it
     * stayed pending for ever: not members, invisible to their own leadership, and told "you cannot
     * join this hall" if they tried again. Turning approval off now admits them (the server does
     * it), and this is the belt to that braces: a queue that exists is a queue that is drawn,
     * whatever the policy that produced it.
     */
    if (hallId === null || !iLead) { setPending([]); setPendingUnread(false); return; }
    try {
      setPending(await client.hallPending(hallId));
      setPendingUnread(false);
    } catch {
      setPendingUnread(true);
    }
  }, [client, hallId, iLead]);

  useEffect(() => {
    let live = true;
    void loadPending().then(() => { if (!live) setPending([]); });
    return () => { live = false; };
  }, [loadPending]);

  /*
   * THE ROSTER, for the LEADERSHIP — widened from the owner when barring arrived.
   *
   * It was the owner's alone, and the reason was sound at the time: only an owner may appoint a
   * co-host, so a roster a co-host could look at but never act on would be a list of people with no
   * verb. Barring is the verb a co-host does have — it is the mod team's whole job — so the list is
   * now theirs too, with the appointing control still owner-only inside it.
   */
  const loadRoster = useCallback(async (): Promise<void> => {
    if (hallId === null || !iLead) { setRoster([]); setBarred([]); setRosterUnread(false); return; }
    try {
      setRoster(await client.hallRoster(hallId));
      /* And whom they have barred, which `HallView` deliberately does not carry: without it the ban
         has no undo, because a barred reader is no longer on any roster. */
      setBarred(await client.hallBanned(hallId));
      setRosterUnread(false);
    } catch {
      /* Whatever was already on screen stays there: a list that empties itself on a bad moment is
         a leader watching their community disappear. */
      setRosterUnread(true);
    }
  }, [client, hallId, iLead]);


  useEffect(() => { void loadRoster(); }, [loadRoster]);

  /*
   * WHAT THE MOD TEAM HAS DONE (COMMUNITY.md D22).
   *
   * For LEADERSHIP, not only the owner — a co-host supervising alongside the leader is the point of
   * a team, and unlike the roster there is no verb here to be denied: reading is the whole feature.
   *
   * Asked for by nobody else, ever. This says who ejected whom, which is exactly the fact a room's
   * view refuses to publish; the people who may read it are the people who already hold the power
   * it records, so it adds accountability and no authority.
   */
  const loadLog = useCallback(async (): Promise<void> => {
    if (hallId === null || !iLead) { setLog([]); setLogEnded(false); return; }
    let found;
    try {
      found = await client.hallLog(hallId);
    } catch {
      /*
       * NOT `logEnded`, which is the half that made this worst: an empty answer means the record
       * has been read to its end and the control to read further goes away. A failed read that set
       * it told a leader the history was complete when none of it had arrived.
       */
      setLogUnread(true);
      return;
    }
    setLogUnread(false);
    setLog(found);
    setLogEnded(found.length === 0);

  }, [client, hallId, iLead]);

  useEffect(() => {
    let live = true;
    void loadLog().then(() => { if (!live) setLog([]); });
    return () => { live = false; };
  }, [loadLog]);

  /*
   * WALKING BACK THROUGH IT — the half that made this a history rather than a window.
   *
   * The server answers a page at a time and the page was all a leader could ever see. D22 asks for
   * this record so *"a disputed ejection is resolvable"*, and a dispute raised after the hundredth
   * act since was about a row no client could reach: the leader defending their own moderator had
   * no way to produce the evidence.
   *
   * The cursor is the OLDEST row already held, so "earlier" means everything before what is on
   * screen. A page that comes back empty is the end of the record and the control goes away —
   * a button that does nothing is worse than no button.
   */
  /*
   * GOING ON THROUGH A LIST READ FORWARDS — the roster and the queue, which are not records.
   *
   * `earlier` below walks BACK through what happened; these two walk ON through who is here. The
   * cursor is the LAST row already held, and it names the row rather than the moment because two
   * people can join in one millisecond and a page boundary between them would lose the second.
   *
   * A page that FAILED is not the end of the list, and saying it is takes away the only control
   * that could try again — the lesson the record below had to learn twice.
   */
  const moreMembers = useCallback(async (): Promise<void> => {
    const last = roster[roster.length - 1];
    if (hallId === null || !last) return;
    let next;
    try {
      next = await client.hallRoster(hallId, { after: last.joinedAt, afterId: last.id });
    } catch {
      setRosterUnread(true);
      return;
    }
    setRosterUnread(false);
    setRoster(current => [...current, ...next]);
    setRosterEnded(next.length === 0);
  }, [client, hallId, roster]);

  /*
   * AND THE CO-HOST REQUESTS, walked BACK — they are newest first, being a queue of proposals
   * rather than a history of arrivals.
   *
   * **Accept and decline exist nowhere else.** A pending proposal is deliberately absent from the
   * room list — that is the consent rule — so an invitation past the first page was a question
   * this leadership could never answer, and nothing bounds how many rooms may ask at once.
   */
  const moreInvitations = useCallback(async (): Promise<void> => {
    const oldest = invitations[invitations.length - 1];
    if (hallId === null || !oldest) return;
    let older;
    try {
      older = await client.coHostInvitations(hallId, { before: oldest.createdAt, beforeId: oldest.id });
    } catch {
      setInvitationsUnread(true);
      return;
    }
    setInvitationsUnread(false);
    setInvitations(current => [...current, ...older]);
    setInvitationsEnded(older.length === 0);
  }, [client, hallId, invitations]);

  const moreWaiting = useCallback(async (): Promise<void> => {
    const last = pending[pending.length - 1];
    if (hallId === null || !last) return;
    let next;
    try {
      next = await client.hallPending(hallId, { after: last.joinedAt, afterId: last.id });
    } catch {
      setPendingUnread(true);
      return;
    }
    setPendingUnread(false);
    setPending(current => [...current, ...next]);
    setPendingEnded(next.length === 0);
  }, [client, hallId, pending]);

  const earlier = useCallback(async (): Promise<void> => {
    const oldest = log[log.length - 1];
    if (hallId === null || !oldest) return;
    let older;
    try {
      /* The ROW it stopped at, not just the moment: two acts can share a millisecond and a page
         boundary between them used to leave the second unreachable for ever. */
      older = await client.hallLog(hallId, { before: oldest.at, beforeId: oldest.id });
    } catch {
      /*
       * NOT `logEnded` — the same half that made the FIRST read of this record worst, and it was
       * still here in the function beside it. An empty page means the record has been read to its
       * end and the control to read further goes away; a page that FAILED is not that, and saying
       * so takes away the only control that could try again. D22 asks for this record so that a
       * disputed ejection is resolvable, and a leader cannot resolve one from a history the
       * product has told them is complete.
       */
      setLogUnread(true);
      return;
    }
    setLogUnread(false);
    setLog(current => [...current, ...older]);
    setLogEnded(older.length === 0);
  }, [client, hallId, log]);

  /*
   * WHO HAS ASKED US TO CO-HOST (COMMUNITY.md D22).
   *
   * Leadership only, and it must be a separate read: a pending proposal is deliberately absent from
   * the room list on this page, because listing it would put this community's name beside a session
   * it has not agreed to. That is the consent rule, and it means this is the ONLY place the
   * leadership can learn they were asked.
   */
  const [reload, setReload] = useState(0);
  /*
   * AND A FAILURE IS SAID, for the reason the paragraph above gives without drawing the
   * conclusion: if this is the only place the leadership can learn they were asked, then a failed
   * read here is the asking going silent, and it renders as nobody having asked — which is nearly
   * always true and therefore never questioned. Same fix as the door above, for the same reason.
   */
  useEffect(() => {
    if (hallId === null || !iLead) { setInvitations([]); setInvitationsUnread(false); return; }
    let live = true;
    client.coHostInvitations(hallId)
      .then(found => { if (live) { setInvitations(found); setInvitationsUnread(false); } })
      /* What was on screen stays: an invitation that vanishes on a bad moment is a partnership
         nobody declined and nobody accepted. */
      .catch(() => { if (live) setInvitationsUnread(true); });
    return () => { live = false; };
  }, [client, hallId, iLead, reload]);

  /*
   * AND THE TWO THAT NOTHING ELSE CORRECTS, WHEN THE LEADER COMES BACK TO THE TAB.
   *
   * The same criterion twice. The queue at the door and a co-host request are the two lists on
   * this page that are drawn NOWHERE else — that is why each says when it could not be read — and
   * it is the same reason they are worth reading again: a leader with their community open in a
   * tab is exactly who somebody knocks at while they are looking at something else, and nothing
   * would have told them. The Hall itself, the roster and the record are re-read by the acts that
   * change them; these two change because of somebody ELSE.
   *
   * A gesture, not a timer, as on the rooms panel: one request when the leader looks, none while
   * they are away. The invitations read is keyed on `reload`, so bumping it IS asking again.
   */
  useEffect(() => {
    const cameBack = (): void => {
      if (document.visibilityState !== 'visible') return;
      void loadPending();
      setReload(n => n + 1);
    };
    document.addEventListener('visibilitychange', cameBack);
    return () => { document.removeEventListener('visibilitychange', cameBack); };
  }, [loadPending]);

  /*
   * ONE ACT PER PERSON, PER INVITATION, PER HALL. Five controls on this page act on one thing by id
   * and are refused if the act is repeated, BECAUSE THE FIRST ONE WORKED:
   *
   *   approve      `nobody is waiting under that name`
   *   let back in  `that reader is not barred from this hall`
   *   accept       `no such invitation`
   *   decline      `only one of the two halls' leadership can end that`
   *   leave        `you are not a member, or you own this hall`
   *
   * `attempt` below says all five out loud, so a reader who presses twice is told the admission
   * failed, or — worst of them — that they do not lead the community they lead, or are not a member
   * of one they have just left. The row is on screen for the whole round trip, which is the window.
   *
   * JOIN sits beside them and needs no guard, which is worth saying because it looks identical:
   * `HallsService.join` returns the EXISTING membership when there is one, and its own comment is
   * about this very race — *"two presses of Join a few milliseconds apart both pass that read"* —
   * answered at the database rather than here. See `useOneAtATime` for the test that decides which
   * of the two a control is.
   */
  const row = useOneAtATime();

  /**
   * ONE NAMED FUNCTION PER GUARDED CONTROL, rather than `row.run` inlined into six handlers.
   *
   * `verify-comms`'s seam sweep exempts a call whose callee is declared in this file, and says why:
   * *"what it does with a failure is on the screen beside the call"*. Inlined, `row.run` came from
   * another file and was a promise nobody held. Named, the gate and `attempt` are visible together,
   * which is the shape `Channels` has always had.
   */
  const letThemIn = async (userId: string): Promise<void> => row.run(userId, () => changesTheRoster(async () => {
    await client.approveMember(hall?.id ?? '', userId);
    setPending(current => current.filter(m => m.userId !== userId));
  }));

  const letBackIn = async (userId: string): Promise<void> => row.run(userId, () => changesTheRoster(async () => {
    await client.unbanFromHall(hall?.id ?? '', userId);
    setBarred(current => current.filter(one => one.userId !== userId));
  }));

  /** Accept and decline share the key: one invitation, two answers. */
  const acceptCoHosting = async (roomId: string): Promise<void> => row.run(roomId, () => attempt(async () => {
    await client.acceptCoHost(roomId, hallId ?? '');
    setReload(n => n + 1);
  }));

  const declineCoHosting = async (roomId: string): Promise<void> => row.run(roomId, () => attempt(async () => {
    await client.endCoHost(roomId);
    setReload(n => n + 1);
  }));

  /** Leaving and withdrawing are the same route on the same Hall, so they share the key. */
  const leave = async (): Promise<void> => row.run(hall?.id ?? '', () => attempt(async () => {
    await client.leaveHall(hall?.id ?? '');
    await load();
  }));

  const deleteThisHall = async (): Promise<void> => row.run(hall?.id ?? '', () => attempt(async () => {
    await client.deleteHall(hall?.id ?? '');
    /* The page cannot stay: what it described no longer exists. */
    onClose();
  }));

  /** Every write here fails in the same few ways, so they are handled once. */
  const attempt = async (action: () => Promise<unknown>): Promise<void> => {
    setProblem(null);
    setNeedsSignIn(false);
    try {
      await action();
    } catch (caught) {
      if (caught instanceof SocialAuthError) setNeedsSignIn(true);
      else if (caught instanceof RoomClosedToYouError) setProblem(t('hall.cannotJoin'));
      else if (caught instanceof NotAHostError) setProblem(t('hall.ownerOnly'));
      else setProblem(refusalWords(t, caught, 'hall.failed'));
    }
  };

  const takeExport = async (): Promise<void> => attempt(async () => {
    const taken = await client.exportHall(hall?.id ?? '');
    /* Handed to the reader as a file rather than shown: it is theirs to keep, and "download" is
       what taking your data away actually looks like. */
    const blob = new Blob([JSON.stringify(taken, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${slug}-export.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  });

  if (loading) return <section className="hall" aria-label={t('hall.label')}><p>{t('hall.loading')}</p></section>;

  if (!hall) {
    return (
      <section className="hall" aria-label={t('hall.label')}>
        {/* Two different things, and saying the wrong one tells a leader their community is gone. */}
        <p className={unreadable ? 'hall-unreadable' : 'hall-missing'} role={unreadable ? 'alert' : undefined}>
          {t(unreadable ? 'hall.unreadable' : 'hall.missing')}
        </p>
        <button type="button" className="hall-close" onClick={onClose}>{t('hall.back')}</button>
      </section>
    );
  }

  return (
    <section className="hall" aria-label={t('hall.label')}>
      <header className="hall-head">
        <h2 className="hall-name">
          {hall.name}
          {hall.verified && (
            <span className="hall-verified" title={t('hall.verifiedAs', { handle: hall.verified.handle })}>
              {t('hall.verifiedMark')}
            </span>
          )}
        </h2>
        <button type="button" className="hall-close" onClick={onClose}>{t('hall.back')}</button>
      </header>

      {hall.description && <p className="hall-description">{hall.description}</p>}

      {/*
        HOW BIG THIS COMMUNITY IS — the one public fact about a Hall that was missing entirely.

        A page that shows a name, a description and some links says nothing about whether there are
        three people here or three thousand, which is the first thing a reader deciding to join
        wants and the first thing a leader wants to show. A COUNT and never the roster: names on a
        public list is the platform-wide social graph D21 refuses.
      */}
      {/*
        AND THE CEILING, to whoever it can change something for — `hallPlaces` decides who that is.
        `memberLimit` is sent in every Hall view and was read by nothing, so a leader learned it
        existed by being REFUSED: they work the queue below, admit somebody, and are told "the
        community is full" a moment after knowing would have let them choose differently. A stranger
        is shown it only when the Hall actually is full, which is the one moment the number answers
        what they were about to do.
      */}
      <p className="hall-size">
        {places
          ? t('hall.memberOfLimit', { count: String(places.taken), limit: String(places.limit) })
          : t('hall.memberCount', { count: String(hall.memberCount) })}
      </p>

      {hall.links.length > 0 && (
        <ul className="hall-links">
          {hall.links.map((link: HallLink) => (
            <li key={link.url}>
              {/* Straight there. No nofollow, no interstitial, no rewriting (D21.2). */}
              <a href={link.url} target="_blank" rel="noreferrer">{link.label || link.url}</a>
            </li>
          ))}
        </ul>
      )}

      {/*
        WHAT IS COMING UP — and this is the strip that matters most on this page.

        A Hall is quiet most of the time, so a page that could only show live rooms would show a
        leader's community as empty whenever nobody happened to be talking. "Our study is Thursday
        at 20:00" is true at 3am on a Tuesday, which is exactly when someone follows a link from a
        YouTube description (COMMUNITY.md §5). It is listed before the live rooms for that reason.
      */}
      {(sessions.length > 0 || sessionsUnread) && (
        <section className="hall-sessions" aria-label={t('hall.comingUp')}>
          <h3>{t('hall.comingUp')}</h3>
          {sessionsUnread && (
            <p className="hall-unread" role="alert">{t('shared.couldNotRead')}</p>
          )}
          <ul>
            {sessions.map(session => (
              <li key={session.id} className="hall-session">
                <span className="hall-session-title">{session.title ?? nodeName(session.nodeId)}</span>
                <span className="hall-session-where">{nodeName(session.nodeId)}</span>
                <span className="hall-session-when">
                  {/* The date, or the DISTANCE inside the hour — the shared rule, because this is
                      the page a link in a YouTube description lands on: somebody arriving at ten to
                      eight was being given the date of an evening they were already standing in. */}
                  {sessionWhenText(session.startsAt, lang, {
                    unknown: t('shared.timeUnknown'),
                    now: t('sessions.startingNow'),
                    inMinutes: count => t('sessions.startsIn', { count: String(count) }),
                    lasts: minutes => t('sessions.lasts', { count: String(minutes) }),
                  }, new Date(minuteTick), session.durationMinutes)}
                </span>
                {/* SAID HERE TOO, and this is the page it matters on most: a Hall's page is what a
                    link in a YouTube description lands on, so a stranger reading "Thursday 20:00"
                    should learn here that the class is the community's own rather than at its
                    door. Same badge as a locked room, same words. */}
                {session.access === 'passphrase' && <span className="rooms-locked">{t('rooms.locked')}</span>}
                {/* AND WHETHER IT COMES ROUND. On a node's list this is a detail; on a community's
                    page it is the standing invitation — "Thursday 20:00, every week" is the whole
                    of what a link in a YouTube description is promising, and this page said it once
                    and left the reader to guess whether it happens again. */}
                {session.repeat === 'weekly' && <span className="session-repeat">{t('sessions.weekly')}</span>}
                {/* A room exists only once the session is LIVE (D23) — a scheduled one is a
                    promise, not a place, so there is nothing to walk into yet. */}
                {session.status === 'live' && session.roomId !== null && (
                  <button type="button" className="hall-session-join" onClick={() => onGoToNode(session.nodeId)}>
                    {t('sessions.joinNow')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        WHERE THIS COMMUNITY IS TALKING RIGHT NOW — the thing a leader's link should land on when
        there is something to walk into. Rendered only when there IS: a permanent "nobody is here"
        on a leader's public page advertises a dead community, and the strip is the wrong place to
        offer opening one (a room is opened on the node it is about).

        A whole row is the target rather than a small "join" control — the row is what the reader is
        already looking at.
      */}
      {rooms.length > 0 && (
        <section className="hall-rooms" aria-label={t('hall.talkingNow')}>
          <h3>{t('hall.talkingNow')}</h3>
          <ul>
            {rooms.map(room => (
              <li key={room.id}>
                <button type="button" className="hall-room" onClick={() => onGoToNode(room.nodeId)}>
                  {/* An unnamed room shows the NODE's name, resolved by the caller (D10). */}
                  <span className="hall-room-title">{room.title ?? nodeName(room.nodeId)}</span>
                  <span className="hall-room-where">{nodeName(room.nodeId)}</span>
                  <span className="hall-room-count">
                    {room.occupancy > 0
                      ? t('hall.roomHere', { count: String(room.occupancy) })
                      : t('hall.roomQuiet')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        ONE CONTROL, THE ONE THAT APPLIES — and it was three wrong ones.

        Join and Leave were rendered side by side, to everyone, always: a member invited to join, a
        stranger invited to leave, and a reader who had knocked at an approval Hall shown both and
        told nothing at all about waiting. Each of those presses answers 400, and a control that
        cannot work reads as a broken product rather than as a rule.

        The waiting line is the applicant's half of the queue whose leader-facing half was built
        first — the leader could see who was knocking, and the person knocking could not see that
        they had. Withdrawing is offered beside it, because leaving is free (D21.1) and that has to
        include leaving a queue.

        The owner is offered NOTHING here: a Hall with nobody accountable for it is what the server
        refuses, so a Leave button on their own page would be a lie in the shape of a control.
      */}
      <div className="hall-membership">
        {/* Nothing at all when we could not tell: every control here would be a guess about this
            reader, and the wrong one invites a member to join what they are already in. */}
        {standingUnknown && <p className="hall-standing-unknown" role="alert">{t('hall.standingUnknown')}</p>}
        {!standingUnknown && hallControl(standing) === 'join' && (
          <button type="button" className="hall-join" onClick={() => void attempt(async () => { await client.joinHall(hall.id); await load(); })}>
            {t('hall.join')}
          </button>
        )}
        {!standingUnknown && hallControl(standing) === 'waiting' && (
          <>
            <p className="hall-waiting-note">{t('hall.waitingToBeLetIn')}</p>
            <button
              type="button"
              className="hall-leave"
              onClick={() => void leave()}
              disabled={row.working(hall.id)}
            >
              {t('hall.withdraw')}
            </button>
          </>
        )}
        {!standingUnknown && hallControl(standing) === 'leave' && (
          <button
            type="button"
            className="hall-leave"
            /* The same key as WITHDRAW above: one route, one Hall, and the two never draw together. */
            onClick={() => void leave()}
            disabled={row.working(hall.id)}
          >
            {t('hall.leave')}
          </button>
        )}
      </div>

      {/*
        THE DOOR — visible only when the Hall actually holds people at it.

        `memberPolicy: 'approval'` put a joiner in `status: 'pending'`, and every read path in the
        product filtered to `active`. So the queue existed, nobody could see it, and the approve
        endpoint had no way to be given a name. A leader who chose approval stranded every applicant
        permanently, and neither side could tell: the applicant had joined as far as they knew.
      */}
      {iLead && (pending.length > 0 || pendingUnread) && (
        <section className="hall-pending" aria-label={t('hall.waiting')}>
          <h3>{t('hall.waiting')}</h3>
          {pendingUnread && (
            <p className="hall-unread" role="alert">{t('shared.couldNotRead')}</p>
          )}
          <ul>
            {pending.map(member => (
              <li key={member.userId} className="hall-pending-member">
                {/*
                  THE NAME. This rendered `member.userId` until the a11y suite was pointed at a
                  populated queue: the endpoint was extended to carry `displayName` two commits after
                  this row was written, and the row was never moved over — so a leader was still
                  being asked to admit "68b1f4…" by sight, which is the exact thing that made the
                  approval queue unusable in the first place.

                  A departed reader keeps their row and their verb, for the reason the mod team's
                  rows do: a placeholder name would be a small lie, and the row still has to work.
                */}
                <span className="hall-pending-who">{member.displayName ?? t('hall.departed')}</span>
                {/*
                  WHEN THEY ASKED — the server has always sent it and nothing has ever shown it.

                  The queue arrives oldest-first, so the ORDER carries this and the duration does
                  not: a leader looking at three names cannot tell whether the top one knocked an
                  hour ago or a fortnight ago, and those are different decisions about the same
                  person. It is the only fact the row has besides a name.
                */}
                <time className="hall-pending-when" dateTime={member.joinedAt}>
                  {t('hall.asked', { when: moment(member.joinedAt) })}
                </time>
                <button
                  type="button"
                  className="hall-approve"
                  onClick={() => void letThemIn(member.userId)}
                  disabled={row.working(member.userId)}
                >
                  {t('hall.approve')}
                </button>
              </li>
            ))}
            {/* Drawn until a page comes back empty. Admitting and refusing are drawn on a ROW, so
                an applicant nobody can reach waits for ever — the state this queue exists to end. */}
            {!pendingEnded && (
              <li className="hall-pending-member">
                <button type="button" className="hall-more" onClick={() => void moreWaiting()}>
                  {t('hall.showMore')}
                </button>
              </li>
            )}
          </ul>
        </section>
      )}

      {/*
        THE MOD TEAM — what D22 calls the point of a Hall, and what had no control anywhere.
        `setModerator` was answered by the server, reached by no client, and depended on by the
        Hall-scoped room moderation built three commits earlier: a co-host moderates that Hall's
        rooms without hosting each one. No leader could ever acquire one.

        Owner only, because a co-host cannot appoint co-hosts — the server says so and this agrees
        rather than offering a control that will be refused.
      */}
      {/*
        WIDENED FROM THE OWNER TO THE LEADERSHIP, because of what is now in it. Appointing a
        co-host stays the owner's alone — the server says so — but BARRING somebody is a co-host's
        job too, and it is what the mod team D22 describes exists to do.
      */}
      {/*
        THE GATE ACCOUNTS FOR THE FAILURE, which is what the notice being outside it was working
        around. This section drew only when the roster HAD entries — precisely the state a failed
        read produces — so the message could not be placed inside it and sat above instead: a bare
        sentence in the middle of a page, with nothing saying which of this page's five lists it
        was about. Four of them can say it at once.

        Opening the section when the read failed puts the sentence under the heading that names it,
        and keeps the rule that produced the workaround: a section that says "nobody" every day is
        furniture, and this one still appears only when there is something to say.
      */}
      {iLead && (roster.length > 0 || rosterUnread) && (
        <section className="hall-team" aria-label={t('hall.team')}>
          <h3>{t('hall.team')}</h3>
          {rosterUnread && (
            <p className="hall-unread" role="alert">{t('shared.couldNotRead')}</p>
          )}
          <ul>
            {roster.filter(member => member.userId !== hall.ownerId).map(member => {
              const isCoHost = hall.moderatorIds.includes(member.userId);
              return (
                <li key={member.userId} className="hall-team-member">
                  {/* A reader who deleted their account leaves a row that still has to work — a
                      stale co-host is exactly who somebody needs to stand down. */}
                  <span className="hall-team-who">{member.displayName ?? t('hall.departed')}</span>
                  {isCoHost && <span className="hall-team-badge">{t('hall.coHost')}</span>}
                  {iOwn && (
                    <button
                      type="button"
                      className="hall-team-set"
                      onClick={() => void attempt(async () => {
                        setHall(await client.setHallModerator(hall.id, member.userId, !isCoHost));
                      })}
                    >
                      {isCoHost ? t('hall.standDown') : t('hall.makeCoHost')}
                    </button>
                  )}
                  {/*
                    BAR THEM FROM THE COMMUNITY — answered by the server since Halls existed and
                    reachable from nowhere until now. The list it feeds is below; without that, this
                    would be a one-way door, which is the mistake a room's ban made first.

                    It reaches the ROOMS as well as the roster: whoever is in the community's live
                    study when this is pressed is disconnected from it.
                  */}
                  <button
                    type="button"
                    className="hall-bar"
                    onClick={() => void changesTheRoster(async () => {
                      await client.banFromHall(hall.id, member.userId);
                      setRoster(current => current.filter(m => m.userId !== member.userId));
                      setBarred(current => [...current, { userId: member.userId, displayName: member.displayName }]);
                    })}
                  >
                    {t('hall.bar', { name: member.displayName ?? t('hall.departed') })}
                  </button>
                </li>
              );
            })}
            {/* Drawn until a page comes back empty. Barring and appointing are drawn on a ROW, so
                a member nobody can reach is a member nobody can act on. */}
            {!rosterEnded && (
              <li className="hall-member">
                <button type="button" className="hall-more" onClick={() => void moreMembers()}>
                  {t('hall.showMore')}
                </button>
              </li>
            )}
          </ul>
        </section>
      )}

      {/*
        WHOM THIS COMMUNITY HAS BARRED — leadership only, and absent when nobody is.

        `HallView` omits `bannedIds` on purpose, so this is the only way a leader can see whom they
        turned away. Without it the ban has no undo: a barred reader is no longer a member, so they
        appear in no roster and there is nothing left to name them by.
      */}
      {iLead && barred.length > 0 && (
        <section className="hall-barred" aria-label={t('hall.barred')}>
          <h3>{t('hall.barred')}</h3>
          <ul>
            {barred.map(person => (
              <li key={person.userId} className="hall-barred-row">
                <span className="hall-barred-who">{person.displayName ?? t('hall.departed')}</span>
                <button
                  type="button"
                  className="hall-unbar"
                  onClick={() => void letBackIn(person.userId)}
                  disabled={row.working(person.userId)}
                >
                  {t('hall.letBackIn', { name: person.displayName ?? t('hall.departed') })}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        SOMEBODY HAS ASKED US TO CO-HOST (COMMUNITY.md D22).

        High on the page, because it is a question waiting for an answer rather than a fact to read
        — and absent entirely when nobody has asked, which is nearly always.

        The room's NODE names it, not the other Hall: the social plane stores node ids and never
        node names (D10), and the caller resolves them. What a leader needs to decide is "which
        conversation, and where", which is what a passage answers.
      */}
      {iLead && (invitations.length > 0 || invitationsUnread) && (
        <section className="hall-invites" aria-label={t('hall.invited')}>
          <h3>{t('hall.invited')}</h3>
          {invitationsUnread && (
            <p className="hall-unread" role="alert">{t('shared.couldNotRead')}</p>
          )}
          <ul className="hall-invites-list">
            {invitations.map(room => (
              <li key={room.id} className="hall-invite">
                <span className="hall-invite-what">
                  {room.title ?? nodeName(room.nodeId)}
                  <span className="hall-invite-where">{nodeName(room.nodeId)}</span>
                </span>
                <button
                  type="button"
                  className="hall-invite-yes"
                  onClick={() => void acceptCoHosting(room.id)}
                  disabled={row.working(room.id)}
                >
                  {t('hall.acceptCoHost')}
                </button>
                {/* Declining and withdrawing are one write, because they are one fact: the
                    relationship is not happening. */}
                <button
                  type="button"
                  className="hall-invite-no"
                  /* The same key as ACCEPT above: one invitation, two answers, and pressing both is
                     the same race as pressing one twice. */
                  onClick={() => void declineCoHosting(room.id)}
                  disabled={row.working(room.id)}
                >
                  {t('hall.declineCoHost')}
                </button>
              </li>
            ))}
            {/* Drawn until a page comes back empty. Accept and decline exist nowhere else, so an
                invitation nobody can reach is a partnership nobody can answer. */}
            {!invitationsEnded && (
              <li className="hall-invite">
                <button type="button" className="hall-more" onClick={() => void moreInvitations()}>
                  {t('hall.showMore')}
                </button>
              </li>
            )}
          </ul>
        </section>
      )}

      {/*
        WHAT THE TEAM HAS DONE (COMMUNITY.md D22) — beside the team itself, because that is where a
        leader is standing when the question occurs to them.

        Absent when there is nothing to show, rather than an empty heading: a new Hall whose
        moderators have done nothing yet is the common case, and a section that only ever says "no
        entries" is furniture. Read-only by design — there is no verb here, and there should not be:
        a log with an edit button is not a log.
      */}
      {iLead && (log.length > 0 || logUnread) && (
        <section className="hall-log" aria-label={t('hall.log')}>
          <h3>{t('hall.log')}</h3>
          {logUnread && (
            <p className="hall-unread" role="alert">{t('shared.couldNotRead')}</p>
          )}
          <ul className="hall-log-list">
            {log.map(entry => (
              <li key={entry.id} className="hall-log-row">
                {/* Whoever has gone reads as "a departed reader" and not as a blank — see
                    `HallLogEntry`, where the row deliberately outlives the account. */}
                <span className="hall-log-who">{entry.actor ?? t('hall.departed')}</span>
                {/* The name is always passed and simply unused by the room-wide phrases, which
                    carry no `{name}` token — one call rather than two branches. */}
                <span className="hall-log-what">{t(saidBy(entry.action), { name: entry.subject ?? '' })}</span>
                <time className="hall-log-when" dateTime={entry.at}>{moment(entry.at)}</time>
              </li>
            ))}
          </ul>
          {/*
            Offered whenever the end has not been reached, and NOT compared against the server's
            page size — that number is the server's and a copy of it here would be a second one to
            keep in step. A Hall with nine entries costs one click to learn there are no more, and
            the control then goes away for good.
          */}
          {/*
            AND ONLY WITH A ROW TO WALK BACK FROM. `earlier` takes the OLDEST row held as its
            cursor and returns at once when there is none — so with the section now opening on a
            failed read, an empty log would have drawn a control that does nothing, which the
            paragraph above this function calls worse than no control at all. The sentence saying
            the read failed is what belongs there, and it is directly above.
          */}
          {log.length > 0 && !logEnded && (
            <button type="button" className="hall-log-earlier" onClick={() => void earlier()}>
              {t('hall.logEarlier')}
            </button>
          )}
        </section>
      )}

      {/*
        THE FRONT PAGE ITSELF, which a leader could not change.

        `PATCH /halls/:id` was answered by the server and reached by no renderer — so the links that
        D21.2 promises to render plainly were links nobody could ADD, and `memberPolicy` (the switch
        that makes the approval queue exist) had no surface anywhere.
      */}
      {iOwn && (
        <section className="hall-edit" aria-label={t('hall.settings')}>
          <h3>{t('hall.settings')}</h3>
          <HallSettings
            client={client}
            hall={hall}
            /*
             * AND WHAT THE SAVE DID TO THE ROSTER, which is not obvious from the panel above it.
             *
             * Setting `memberPolicy` to `open` ADMITS whoever was waiting — the server does it in
             * the same request (`admitAllPending`), because *"that is what 'I no longer require
             * approval' means"*. Only `hall` was taken from the answer, so the page went on
             * drawing those people at the door with an Approve button for somebody who was already
             * a member, over a roster that did not have them, under a count that did — the same
             * disagreement `changesTheRoster` exists to prevent, arriving by a door it does not
             * cover.
             */
            onSaved={saved => {
              setHall(saved);
              setProblem(null);
              void loadPending();
              void loadRoster();
            }}
            onProblem={setProblem}
          />
        </section>
      )}

      {iOwn && (
        <section className="hall-owner" aria-label={t('hall.ownerTools')}>
          <h3>{t('hall.ownerTools')}</h3>

          {/* The door out, in plain sight (D21.3). */}
          <button type="button" className="hall-export" onClick={() => void takeExport()}>
            {t('hall.export')}
          </button>

          {/*
            AND THE DOOR OUT OF THE PRODUCT ENTIRELY (D21.4).

            `COMMUNITY.md` lists "a Hall is deletable, and deletion is real" as one of four trust
            commitments, and says two of them are deliberately the opposite of what a
            growth-optimised design would choose — "that is the evidence that the sentence in rule 3
            was taken literally". The evidence had no surface: `DELETE /halls/:id` was answered by
            the server and reached by no renderer.

            It sits AFTER the export, in that order on purpose: a leader taking their community
            elsewhere should be handed their roster before they are offered the button that destroys
            it. Confirmed by typing the address rather than by a browser dialog — the standard shape
            for something irreversible, and it makes the act deliberate without nagging anybody who
            was only reading the page.
          */}
          {!deleting ? (
            <button type="button" className="hall-delete" onClick={() => setDeleting(true)}>
              {t('hall.delete')}
            </button>
          ) : (
            <div className="hall-delete-confirm">
              <label>
                {t('hall.deleteConfirm', { slug: hall.slug })}
                <input
                  type="text"
                  value={deleteTyped}
                  onChange={event => setDeleteTyped(event.target.value)}
                  aria-label={t('hall.deleteConfirm', { slug: hall.slug })}
                />
              </label>
              <button
                type="button"
                className="hall-delete-final"
                disabled={deleteTyped.trim() !== hall.slug || row.working(hall.id)}
                onClick={() => void deleteThisHall()}
              >
                {t('hall.deleteFinal')}
              </button>
              <button type="button" className="hall-delete-cancel" onClick={() => { setDeleting(false); setDeleteTyped(''); }}>
                {t('hall.deleteCancel')}
              </button>
            </div>
          )}

          {!hall.verified && (
            <form
              className="hall-verify"
              onSubmit={event => {
                event.preventDefault();
                void attempt(async () => setChallenge(await client.startHallVerification(hall.id, handle)));
              }}
            >
              <input
                type="text"
                value={handle}
                onChange={event => setHandle(event.target.value)}
                placeholder={t('hall.handlePlaceholder')}
                aria-label={t('hall.handlePlaceholder')}
                maxLength={HALL_LIMITS.linkUrlMaxLength}
              />
              <button type="submit">{t('hall.verify')}</button>
            </form>
          )}

          {challenge && (
            <div className="hall-challenge">
              <p>{t('hall.challengeLead', { handle: challenge.handle })}</p>
              {/* Selectable, because the leader has to paste it somewhere else. */}
              <code className="hall-token">{challenge.token}</code>
              <button
                type="button"
                onClick={() => void attempt(async () => { setHall(await client.checkHallVerification(hall.id)); setChallenge(null); })}
              >
                {t('hall.checkNow')}
              </button>
            </div>
          )}
        </section>
      )}

      {needsSignIn && (
        <p className="hall-signin" role="alert">
          <a href={client.signInUrl()}>{t('auth.google')}</a>{t('hall.toTakePart')}
        </p>
      )}
      {problem && <p className="hall-problem" role="alert">{problem}</p>}
    </section>
  );
}
